import { Controller } from '@hotwired/stimulus';
import getOverflowedEdges from '../../utilities/edge-detection';
import { afterNextRender } from '../../utilities/timing';
import { Positions, flipPosition } from '../../utilities/position';
import { getFocusableElements } from '@/global/scripts/utilities/focus';

const toolTipTriggerEvents = [
  'mouseover',
  'focusin',
] as (keyof HTMLElementEventMap)[];
const toolTipDismissEvents = [
  'mouseleave',
  'focusout',
] as (keyof HTMLElementEventMap)[];

const dialogTriggerEvents = ['click'] as (keyof HTMLElementEventMap)[];

export default class extends Controller<HTMLElement> {
  static readonly classes = [...Object.keys(Positions), 'hidden'];
  static readonly targets = ['trigger', 'tooltip'];
  static readonly values = {
    position: String,
  };

  declare hiddenClasses: string[];
  declare leftClasses: string[];
  declare rightClasses: string[];
  declare bottomClasses: string[];
  declare topClasses: string[];
  declare topLeftClasses: string[];
  declare topRightClasses: string[];
  declare bottomLeftClasses: string[];
  declare bottomRightClasses: string[];
  declare defaultClasses: string[];
  declare triggerTarget: HTMLElement;
  declare tooltipTarget: HTMLElement;
  declare positionValue: Positions;

  private isDialogRole = false;
  private isShown = false;

  initialize() {
    super.initialize();

    this.hide();
    this.isDialogRole = this.tooltipTarget.role == 'dialog';
    this.tooltipTarget.classList.add(
      ...this.getPositionClasses(this.positionValue),
    );

    if (this.isDialogRole) {
      this.makeDialog();
    } else {
      for (const eventType of toolTipTriggerEvents) {
        this.element.addEventListener(eventType, this.show);
      }

      for (const eventType of toolTipDismissEvents) {
        this.element.addEventListener(eventType, this.hide);
      }
    }

    afterNextRender(this.adjustAndApplyPosition);
  }

  get disabled() {
    if (this.isDialogRole) {
      return (this.triggerTarget as HTMLButtonElement).disabled;
    }

    return this.triggerTarget.getAttribute('tabindex') !== '0';
  }

  set disabled(disabled: boolean) {
    if (this.isDialogRole) {
      (this.triggerTarget as HTMLButtonElement).disabled = disabled;
    } else if (disabled) {
      this.triggerTarget.removeAttribute('tabindex');
    } else {
      this.triggerTarget.setAttribute('tabindex', '0');
    }
  }

  disable() {
    this.disabled = true;
  }

  enable() {
    this.disabled = false;
  }

  hide = () => {
    this.isShown = false;
    this.tooltipTarget.classList.add(...this.hiddenClasses);
  };

  show = () => {
    if (this.disabled) return false;

    this.isShown = true;
    afterNextRender(() => {
      this.tooltipTarget.classList.remove(...this.hiddenClasses);
      this.tooltipTarget.classList.add(
        ...this.getPositionClasses(this.positionValue),
      );

      afterNextRender(this.adjustAndApplyPosition);
    });
  };

  private adjustAndApplyPosition = () => {
    const oldPosition = this.positionValue;
    this.adjustPosition();
    this.tooltipTarget.classList.remove(
      ...this.getPositionClasses(oldPosition),
    );
    this.tooltipTarget.classList.add(
      ...this.getPositionClasses(this.positionValue),
    );
  };

  private getPositionClasses(position: Positions): string[] {
    let indexOfClass = Object.values(Positions).indexOf(position);
    indexOfClass = indexOfClass === -1 ? 0 : indexOfClass;
    const key = Object.keys(Positions)[indexOfClass];
    const classKey = `${key}Classes` as keyof typeof this;
    return this[classKey] as string[];
  }

  private adjustPosition() {
    const overflowedEdges = getOverflowedEdges(this.tooltipTarget);

    if (overflowedEdges.top) {
      this.positionValue = flipPosition(this.positionValue, Positions.bottom);
    } else if (overflowedEdges.bottom) {
      this.positionValue = flipPosition(this.positionValue, Positions.top);
    }

    if (overflowedEdges.right) {
      this.positionValue = flipPosition(this.positionValue, Positions.left);
    } else if (overflowedEdges.left) {
      this.positionValue = flipPosition(this.positionValue, Positions.right);
    }
  }

  private makeDialog() {
    const focusable = getFocusableElements(this.tooltipTarget);
    for (const eventType of dialogTriggerEvents) {
      this.triggerTarget.addEventListener(eventType, () => {
        if (this.isShown) {
          this.hide();
          this.triggerTarget.focus();
          this.triggerTarget.ariaExpanded = 'false';
        } else {
          this.show();
          this.triggerTarget.ariaExpanded = 'true';

          afterNextRender(() => {
            if (focusable.length > 0) {
              focusable[0].focus();
            }
          });
        }
      });
    }
  }
}
